import { Link } from 'react-router-dom'
import { SectionHeading, SiteFooter, SiteHeader } from './SiteChrome'
import { useScrollToAnchor } from './useScrollToAnchor'
import { aadFundUrl, donatePaymentUrl } from './siteMenu'

const impactAreas = [
  {
    title: 'Artist support',
    body: 'Direct funding for artists and productions that would otherwise never be made.',
  },
  {
    title: 'Creative education',
    body: 'AAN EDUC8 masterclasses, workshops and mentorship for emerging creatives.',
  },
  {
    title: 'Cultural exchange',
    body: 'Touring, residencies and exchange that carry African work beyond its home city.',
  },
  {
    title: 'Platforms & access',
    body: 'The screens, stages and platforms that connect African talent with wider audiences.',
  },
]

export default function DonatePage() {
  useScrollToAnchor()

  return (
    <main className="aan-site min-h-screen bg-black text-white">
      <SiteHeader />

      <section className="border-b border-white/10 bg-black px-6 pb-14 pt-12 sm:pb-16 sm:pt-16">
        <div className="mx-auto max-w-[1080px]">
          <nav aria-label="Breadcrumb" className="font-display text-[11px] font-black uppercase tracking-[0.24em] text-white/45">
            <Link className="transition hover:text-aan-bright" to="/">
              Home
            </Link>
            <span className="px-2">/</span>
            <span className="text-aan-bright">Donate</span>
          </nav>
          <h1 className="mt-6 font-display text-4xl font-black uppercase leading-none text-white sm:text-5xl lg:text-6xl">
            Support the future of arts in Africa
          </h1>
          <p className="mt-7 max-w-3xl text-lg leading-8 text-white/75">
            The Africa Arts Development Fund supports artists, cultural workers and creative institutions across the
            continent through funding, opportunities and development programmes.
          </p>
        </div>
      </section>

      <section className="px-6 py-14 sm:py-16 lg:py-20">
        <div className="mx-auto grid max-w-[1080px] gap-8 lg:grid-cols-[1.05fr_0.95fr] lg:items-stretch">
          <div className="border-l-4 border-aan-bright pl-6">
            <p className="font-display text-[17px] font-black uppercase leading-tight text-aan-bright sm:text-[22px]">
              Where your donation goes
            </p>
            <div className="mt-6 space-y-5 text-sm leading-7 text-white/80 sm:text-base">
              <p>
                The Africa Arts Development Fund supports artists, cultural workers, and creative institutions across
                the continent through funding, opportunities, and development programs.
              </p>
              <p>
                Your donation helps sustain creative education, artist support, cultural exchange, and platforms that
                connect African talent with wider audiences.
              </p>
              <p>
                Every contribution — one-off or recurring — goes into work that would otherwise struggle to find
                funding: a first film, a training place, a community programme, a stage.
              </p>
            </div>
            <a
              className="mt-7 inline-flex items-center justify-center rounded-full border border-aan-bright px-7 py-3 font-display text-xs font-black uppercase tracking-[0.14em] text-aan-bright transition hover:bg-aan-bright hover:text-black"
              href={aadFundUrl}
              rel="noreferrer"
              target="_blank"
            >
              Read more about AADF
            </a>
          </div>
          <div className="flex flex-col justify-center rounded-[8px] border border-aan-bright bg-aan-footer px-7 py-9 text-center shadow-glow sm:px-10">
            <p className="font-display text-[13px] font-semibold uppercase tracking-[0.24em] text-white/70">
              Africa Arts Development Fund
            </p>
            <h2 className="mt-4 font-display text-4xl font-black uppercase leading-none text-white sm:text-5xl">
              Donate to AAD Fund
            </h2>
            <p className="mx-auto mt-5 max-w-sm text-sm leading-6 text-white/75">
              Continue to the secure Paystack donation page to make your contribution.
            </p>
            <a
              className="mx-auto mt-8 inline-flex min-h-12 items-center justify-center rounded-full bg-aan-bright px-8 font-display text-sm font-black uppercase tracking-[0.12em] text-black transition hover:bg-white"
              href={donatePaymentUrl}
              rel="noreferrer"
              target="_blank"
            >
              Donate Now
            </a>
          </div>
        </div>
      </section>

      <section className="bg-black px-6 pb-14 sm:pb-16 lg:pb-20">
        <SectionHeading>Your impact</SectionHeading>
        <div className="mx-auto mt-12 grid max-w-[1080px] gap-5 sm:grid-cols-2">
          {impactAreas.map((area) => (
            <div className="rounded-[12px] border border-white/15 bg-neutral-950 p-6" key={area.title}>
              <h3 className="font-display text-lg font-black uppercase leading-tight text-aan-bright">{area.title}</h3>
              <p className="mt-3 text-sm leading-7 text-white/75">{area.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="px-6 pb-16 sm:pb-20">
        <div className="mx-auto max-w-[1080px] rounded-[12px] border border-white/15 bg-neutral-950 px-7 py-10 text-center sm:px-12">
          <h2 className="font-display text-3xl font-black uppercase leading-none text-white sm:text-4xl">
            Other ways to give
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-base leading-7 text-white/75">
            Organisations and institutions can support the fund through structured partnerships, sponsorship of a
            production or programme, or long-term institutional funding.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <Link
              className="inline-flex min-h-12 items-center justify-center rounded-full bg-aan-bright px-7 font-display text-xs font-black uppercase tracking-[0.14em] text-black transition hover:bg-white"
              to="/join/partners"
            >
              Become a partner
            </Link>
            <Link
              className="inline-flex min-h-12 items-center justify-center rounded-full border border-aan-bright px-7 font-display text-xs font-black uppercase tracking-[0.14em] text-aan-bright transition hover:bg-aan-bright hover:text-black"
              to="/contact"
            >
              Talk to the team
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter />
    </main>
  )
}
